import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Stethoscope, Video, Phone, Calendar, Circle } from 'lucide-react';
import { CallBalanceBadge } from '../components/CallBalanceBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DoctorsListPage = () => {
  const { t, language } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category') || 'doctors';
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Hard auth-gate: visitors must register/login before browsing doctors,
  // pharmacists, or physiotherapists. Redirect to the register page with a
  // `next` param so they come back here after signup.
  useEffect(() => {
    if (!authLoading && !user) {
      const next = `/doctors?category=${category}`;
      navigate(`/register?next=${encodeURIComponent(next)}`, { replace: true });
    }
  }, [authLoading, user, category, navigate]);

  useEffect(() => {
    const fetchDoctors = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/doctors?category=${category}`);
        setDoctors(response.data);
      } catch (error) {
        console.error('Error fetching doctors:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDoctors();
    // Refresh every 30s to keep availability/online status fresh
    const interval = setInterval(fetchDoctors, 30000);
    return () => clearInterval(interval);
  }, [category]);

  const titles = {
    doctors: { ar: 'الأطباء العامون', en: 'General Doctors', fr: 'Médecins Généralistes' },
    physiotherapy: { ar: 'أخصائيو العلاج الطبيعي', en: 'Physiotherapists', fr: 'Physiothérapeutes' },
    psychology: { ar: 'أخصائيو علم النفس', en: 'Psychologists', fr: 'Psychologues' },
    pharmacy: { ar: 'الصيادلة', en: 'Pharmacists', fr: 'Pharmaciens' }
  };

  const isInstantCall = category === 'doctors' || category === 'pharmacy';

  const handleInstantCall = (doctor, callType) => {
    if (!user) {
      navigate('/login');
      return;
    }
    navigate(`/call/${doctor._id}?type=${callType}`);
  };

  const handleBook = (doctor) => {
    if (!user) {
      navigate('/login');
      return;
    }
    navigate(`/book/${doctor._id}`);
  };

  const getStatusBadge = (doctor) => {
    const available = doctor.is_available !== false;
    const online = doctor.is_online === true;
    
    if (!available) {
      return <Badge variant="secondary" className="bg-gray-200 text-gray-700">{t('غير متاح', 'Unavailable', 'Indisponible')}</Badge>;
    }
    if (online) {
      return (
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
          <Circle size={8} className="me-1 fill-green-500 text-green-500" />
          {t('متصل الآن', 'Online', 'En ligne')}
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="bg-blue-50 text-blue-700">
        {t('متاح', 'Available', 'Disponible')}
      </Badge>
    );
  };

  const title = titles[category] || titles.doctors;

  return (
    <div className={`min-h-screen bg-gray-50 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#2c3e50]" data-testid="doctors-page-title">
            {language === 'ar' ? title.ar : language === 'fr' ? title.fr : title.en}
          </h1>
          {isInstantCall && (
            <p className="text-gray-600 mt-2">
              {t(
                'اتصل مباشرة مع طبيب متاح الآن. سيتم التوصيل خلال 1-3 دقائق',
                'Call an available doctor directly. Connection within 1-3 minutes',
                'Appelez directement un médecin. Connexion en 1-3 minutes'
              )}
            </p>
          )}
          {isInstantCall && (
            <div className="mt-4">
              <CallBalanceBadge />
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e88e5]"></div>
          </div>
        ) : doctors.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-gray-600 text-lg">
              {t('لا يوجد أطباء في هذه الفئة حالياً', 'No doctors in this category yet', 'Aucun médecin dans cette catégorie')}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="doctors-grid">
            {doctors.map((doctor) => {
              const available = doctor.is_available !== false;
              return (
                <Card key={doctor._id} className="p-6 hover:shadow-xl transition-all" data-testid={`doctor-card-${doctor._id}`}>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      {doctor.profile_photo ? (
                        <img src={doctor.profile_photo} alt={doctor.full_name} className="w-16 h-16 rounded-full object-cover" />
                      ) : (
                        <Stethoscope className="text-[#1e88e5]" size={32} />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#2c3e50]">{doctor.full_name}</h3>
                      <p className="text-[#1e88e5] font-semibold text-sm">{doctor.specialty}</p>
                      <div className="mt-2">{getStatusBadge(doctor)}</div>
                    </div>
                  </div>

                  {doctor.biography && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{doctor.biography}</p>
                  )}

                  {isInstantCall ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => handleInstantCall(doctor, 'voice')}
                        disabled={!available}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`voice-call-${doctor._id}`}
                      >
                        <Phone size={18} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
                        {t('صوت', 'Voice', 'Voix')}
                      </Button>
                      <Button
                        onClick={() => handleInstantCall(doctor, 'video')}
                        disabled={!available}
                        className="bg-[#1e88e5] hover:bg-[#1565c0] text-white"
                        data-testid={`video-call-${doctor._id}`}
                      >
                        <Video size={18} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
                        {t('فيديو', 'Video', 'Vidéo')}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => handleBook(doctor)}
                      disabled={!available}
                      className="w-full bg-[#1e88e5] hover:bg-[#1565c0]"
                      data-testid={`book-doctor-${doctor._id}`}
                    >
                      <Calendar size={18} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
                      {t('احجز موعد', 'Book Appointment', 'Prendre rendez-vous')}
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
